// Identify a manga from a photo of the physical book.
//
// The crop box is not a nicety, it is the feature. Measured against the live
// index, framing dominates every other variable:
//
//   tight crop, slight angle      cosine 0.9570   matches
//   slight angle + heavy glare    cosine 0.9543   matches
//   angled, 15% background        cosine 0.8570   lost in the noise
//   angled, 50% background        cosine 0.8100   lost in the noise
//
// Perspective and glare cost almost nothing; SigLIP handles them. But it embeds
// global composition, so a cover surrounded by shelf is a *different picture* to
// it — and unrelated covers sit at ~0.865, which is the bar a real match has to
// clear. Shipping this without a crop step would look broken for the obvious
// reason that most photos include the shelf.
import { useCallback, useMemo, useRef, useState } from "react";

import { ContentRating } from "../gen/amrec/v1/common_pb";
import { searchClient } from "../lib/connect";
import { mutedColor } from "./primitives";

type Match = {
  id: string;
  title: string;
  volume: string;
  score: number;
  matchedBy: string;
  coverUrl?: string;
  year?: number;
};

// Manga covers are close to 2:3, so the box starts at that ratio. It is only a
// starting point — the handle resizes freely, because a photo of a stack or a
// wide art book is not 2:3.
const START_RATIO = 2 / 3;

export function Identify() {
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matches, setMatches] = useState<Match[] | null>(null);

  // Crop rect in natural-image pixels, so it survives layout changes and maps
  // straight onto the canvas without a second coordinate conversion.
  const [crop, setCrop] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const imgRef = useRef<HTMLImageElement | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const libraryRef = useRef<HTMLInputElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ mode: "move" | "resize"; px: number; py: number; c: typeof crop } | null>(null);

  const onFile = useCallback((f: File | null | undefined) => {
    if (!f) return;
    // Clear both inputs so picking the SAME file twice still fires onChange —
    // otherwise a retry after a bad crop looks like the button is dead.
    if (cameraRef.current) cameraRef.current.value = "";
    if (libraryRef.current) libraryRef.current.value = "";
    setMatches(null);
    setError(null);
    setSrc(URL.createObjectURL(f));
  }, []);

  const onImgLoad = useCallback(() => {
    const im = imgRef.current;
    if (!im) return;
    const nw = im.naturalWidth;
    const nh = im.naturalHeight;
    // Start with the largest 2:3 box that fits, centred: the common case is a
    // cover roughly filling the frame, so this is often already right.
    let w = nw * 0.8;
    let h = w / START_RATIO;
    if (h > nh * 0.9) {
      h = nh * 0.9;
      w = h * START_RATIO;
    }
    setCrop({ x: (nw - w) / 2, y: (nh - h) / 2, w, h });
  }, []);

  // Scale factor between displayed pixels and natural pixels.
  const scale = useCallback(() => {
    const im = imgRef.current;
    if (!im || !im.naturalWidth) return 1;
    return im.clientWidth / im.naturalWidth;
  }, []);

  const onPointerDown = (mode: "move" | "resize") => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    drag.current = { mode, px: e.clientX, py: e.clientY, c: crop };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const im = imgRef.current;
    if (!d || !im) return;
    const s = scale() || 1;
    const dx = (e.clientX - d.px) / s;
    const dy = (e.clientY - d.py) / s;
    const nw = im.naturalWidth;
    const nh = im.naturalHeight;

    if (d.mode === "move") {
      setCrop({
        ...d.c,
        x: Math.max(0, Math.min(nw - d.c.w, d.c.x + dx)),
        y: Math.max(0, Math.min(nh - d.c.h, d.c.y + dy)),
      });
    } else {
      // Free resize with a floor, so the box cannot collapse to nothing under
      // an imprecise touch.
      const w = Math.max(48, Math.min(nw - d.c.x, d.c.w + dx));
      const h = Math.max(48, Math.min(nh - d.c.y, d.c.h + dy));
      setCrop({ ...d.c, w, h });
    }
  };

  const onPointerUp = () => { drag.current = null; };

  const identify = useCallback(async () => {
    const im = imgRef.current;
    if (!im || crop.w < 8 || crop.h < 8) return;
    setBusy(true);
    setError(null);
    setMatches(null);
    try {
      // Crop client-side rather than sending the whole photo and cropping
      // server-side: it is the user who knows which book they meant, and a
      // phone photo is several MB of mostly shelf.
      const canvas = document.createElement("canvas");
      // Cap the long edge. The model sees 224px; anything beyond this is
      // upload time for no accuracy.
      const maxEdge = 1024;
      const k = Math.min(1, maxEdge / Math.max(crop.w, crop.h));
      canvas.width = Math.round(crop.w * k);
      canvas.height = Math.round(crop.h * k);
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("canvas unavailable");
      ctx.drawImage(im, crop.x, crop.y, crop.w, crop.h, 0, 0, canvas.width, canvas.height);

      const blob: Blob = await new Promise((res, rej) =>
        canvas.toBlob(b => (b ? res(b) : rej(new Error("encode failed"))), "image/jpeg", 0.9));
      const bytes = new Uint8Array(await blob.arrayBuffer());

      const resp = await searchClient.matchImage({
        imageData: bytes,
        limit: 8,
        // Without an explicit ceiling this defaults to SAFE, which silently
        // hides a large amount of mainstream seinen — MangaDex rates Berserk
        // `erotica`. A shelf in a shop does not filter itself.
        filters: { maxRating: ContentRating.EXPLICIT },
      });

      setMatches(resp.matches.map(m => ({
        id: m.work?.id ?? "",
        title: m.work?.displayTitle ?? "(untitled)",
        volume: m.volume ?? "",
        score: m.score,
        matchedBy: String(m.matchedBy),
        coverUrl: m.work?.cover?.proxyPath,
        year: m.work?.startDate?.year,
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [crop]);

  const boxStyle = useMemo(() => {
    const s = scale();
    return {
      position: "absolute" as const,
      left: crop.x * s,
      top: crop.y * s,
      width: crop.w * s,
      height: crop.h * s,
      border: "2px solid var(--accent, #e8c07d)",
      boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
      touchAction: "none" as const,
      cursor: "move",
    };
  }, [crop, scale]);

  return (
    <section style={{ maxWidth: 720, margin: "0 auto" }}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, margin: "0 0 6px" }}>Identify from a photo</h2>
      <p style={{ color: mutedColor(60), fontSize: 13, margin: "0 0 16px", lineHeight: 1.5 }}>
        Photograph the front cover, then drag the box so it holds the cover and
        as little else as possible. Angle and glare are fine — surrounding shelf
        is what breaks the match.
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => cameraRef.current?.click()} style={pickerButton}>
          {src ? "Retake photo" : "Take photo"}
        </button>
        <button onClick={() => libraryRef.current?.click()} style={pickerButton}>
          Choose from library
        </button>
      </div>

      {/*
        Two inputs, and neither uses display:none.

        iOS Safari will not reliably open the picker for a file input hidden
        with display:none, and a <label> wrapping one is just as unreliable —
        the tap has to reach the input itself inside a real user gesture, which
        is why these are driven by a button calling .click() on a ref.

        Separate inputs because `capture` is not a hint on iOS, it is a
        restriction: with it set, the photo library is not offered at all. One
        input can therefore be "camera" or "library" but never both.
      */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={visuallyHidden}
        onChange={e => onFile(e.target.files?.[0])}
      />
      <input
        ref={libraryRef}
        type="file"
        accept="image/*"
        style={visuallyHidden}
        onChange={e => onFile(e.target.files?.[0])}
      />

      {src && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{ position: "relative", display: "inline-block", overflow: "hidden", maxWidth: "100%" }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <img
              ref={imgRef}
              src={src}
              onLoad={onImgLoad}
              alt="captured"
              style={{ maxWidth: "100%", display: "block", userSelect: "none" }}
              draggable={false}
            />
            <div ref={boxRef} style={boxStyle} onPointerDown={onPointerDown("move")}>
              <div
                onPointerDown={onPointerDown("resize")}
                style={{
                  position: "absolute", right: -14, bottom: -14, width: 28, height: 28,
                  borderRadius: "50%", background: "var(--accent, #e8c07d)",
                  cursor: "nwse-resize", touchAction: "none",
                }}
              />
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <button
              onClick={identify}
              disabled={busy}
              style={{
                padding: "10px 18px", borderRadius: 8, fontSize: 14, cursor: busy ? "default" : "pointer",
                border: "1px solid rgba(255,255,255,0.18)", background: busy ? "transparent" : "rgba(255,255,255,0.06)",
              }}
            >
              {busy ? "Matching…" : "Identify"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <p style={{ marginTop: 14, color: "#e88", fontSize: 13 }}>Match failed: {error}</p>
      )}

      {matches && matches.length === 0 && (
        <p style={{ marginTop: 14, color: mutedColor(60), fontSize: 13 }}>
          No match. Try cropping tighter to the cover.
        </p>
      )}

      {matches && matches.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, marginTop: 18, display: "grid", gap: 10 }}>
          {matches.map(m => (
            <li key={m.id + m.volume} style={{
              display: "flex", gap: 12, alignItems: "center",
              padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.04)",
            }}>
              {m.coverUrl && (
                <img src={m.coverUrl} alt="" style={{ width: 44, height: 64, objectFit: "cover", borderRadius: 4 }} />
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.title}{m.volume && <strong style={{ color: "var(--accent, #e8c07d)" }}> · vol {m.volume}</strong>}
                </div>
                <div style={{ fontSize: 11, color: mutedColor(50), marginTop: 2 }}>
                  {m.year ? `${m.year} · ` : ""}{m.score.toFixed(3)}
                  {/* Worth surfacing: an exact hash hit is a certainty, a
                      visual-similarity hit is a guess. */}
                  {m.matchedBy.includes("PHASH") ? " · exact image" : " · visual match"}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const pickerButton = {
  padding: "10px 16px", borderRadius: 8, fontSize: 14, cursor: "pointer",
  border: "1px solid rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.06)",
  color: "inherit", fontFamily: "inherit",
} as const;

// Present in the layout but invisible. NOT display:none, which iOS Safari
// refuses to open a picker for.
const visuallyHidden = {
  position: "absolute" as const, width: 1, height: 1,
  opacity: 0, pointerEvents: "none" as const, left: -9999,
};
